import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";

import { createQualityCaseEvent, linkQualityCaseEvent, qualityCaseLabel } from "./casesApi";
import {
  correctiveActionSourceLabel,
  qualityEventSourceOptions,
  type CorrectiveActionSource,
} from "./correctiveActionsApi";
import { fetchQualityEvents, qualityEventLabel } from "./qualityEventNcrApi";
import { QualityCustomerPicker } from "./QualityCustomerPicker";

const EVENTS_HELP =
  "Events linked to this case. Search the list, link an event that already exists, or create one and link it. NCRs are on their own card.";

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function QualityCaseEventsCard({ caseId }: { caseId: number }): JSX.Element {
  const writesAllowed = useHubModuleWrite("quality");
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [sourceCategory, setSourceCategory] = useState<CorrectiveActionSource>("supplier");
  const [supplierId, setSupplierId] = useState("");
  const [customerFsAccountId, setCustomerFsAccountId] = useState<number | null>(null);
  const [summary, setSummary] = useState("");
  const [eventIdToLink, setEventIdToLink] = useState("");

  const eventsQuery = useQuery({
    queryKey: ["quality", "events", "case", caseId, search],
    queryFn: () => fetchQualityEvents({ qualityCaseId: caseId, search: search.trim() || undefined, limit: 200 }),
    enabled: Number.isFinite(caseId) && caseId > 0,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["quality", "events", "case", caseId] });
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const parsedSupplier = supplierId.trim() === "" ? null : Number(supplierId.trim());
      if (sourceCategory === "supplier" && (parsedSupplier == null || !Number.isFinite(parsedSupplier) || parsedSupplier < 1)) {
        throw new Error("A supplier event needs a supplier id.");
      }
      if (sourceCategory === "customer_complaint" && customerFsAccountId == null) {
        throw new Error("A customer complaint needs a customer.");
      }
      return createQualityCaseEvent(caseId, {
        sourceCategory,
        summary: summary.trim() || null,
        supplierId: sourceCategory === "supplier" ? parsedSupplier : null,
        customerFsAccountId: sourceCategory === "customer_complaint" ? customerFsAccountId : null,
      });
    },
    onSuccess: async (created) => {
      setShowCreate(false);
      setSupplierId("");
      setCustomerFsAccountId(null);
      setSummary("");
      showToast({ tone: "success", message: `Created ${qualityEventLabel(created.id)}.` });
      await invalidate();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const linkMutation = useMutation({
    mutationFn: (qualityEventId: number) => linkQualityCaseEvent(caseId, qualityEventId),
    onSuccess: async (linked) => {
      setEventIdToLink("");
      showToast({ tone: "success", message: `Linked ${qualityEventLabel(linked.id)}.` });
      await invalidate();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const events = eventsQuery.data?.rows ?? [];
  const filtersActive = search.trim() !== "";

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center gap-2">
        <CardTitleWithHelp hoverHint={EVENTS_HELP}>Linked events</CardTitleWithHelp>
        {writesAllowed && !showCreate ? (
          <Button size="sm" variant="secondary" onClick={() => setShowCreate(true)}>
            New event
          </Button>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-4">
        <div>
          <Label htmlFor={`case-${caseId}-event-search`}>Search events</Label>
          <Input
            id={`case-${caseId}-event-search`}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by event number, source, or summary"
          />
        </div>
        {writesAllowed && showCreate ? (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div>
              <Label htmlFor={`case-${caseId}-event-source`}>Source category</Label>
              <select
                id={`case-${caseId}-event-source`}
                className="mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                value={sourceCategory}
                onChange={(event) => setSourceCategory(event.target.value as CorrectiveActionSource)}
              >
                {qualityEventSourceOptions().map((source) => (
                  <option key={source} value={source}>
                    {correctiveActionSourceLabel(source)}
                  </option>
                ))}
              </select>
            </div>
            {sourceCategory === "supplier" ? (
              <div>
                <Label htmlFor={`case-${caseId}-event-supplier`}>Supplier id</Label>
                <Input
                  id={`case-${caseId}-event-supplier`}
                  value={supplierId}
                  onChange={(event) => setSupplierId(event.target.value)}
                  placeholder="supplier.data id"
                />
              </div>
            ) : null}
            {sourceCategory === "customer_complaint" ? (
              <div>
                <Label htmlFor={`case-${caseId}-event-customer`}>Customer</Label>
                <div className="mt-1.5">
                  <QualityCustomerPicker
                    id={`case-${caseId}-event-customer`}
                    value={customerFsAccountId}
                    onChange={setCustomerFsAccountId}
                  />
                </div>
              </div>
            ) : null}
            <div>
              <Label htmlFor={`case-${caseId}-event-summary`}>Summary</Label>
              <TextArea
                id={`case-${caseId}-event-summary`}
                value={summary}
                rows={3}
                onChange={(event) => setSummary(event.target.value)}
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
                Create event
              </Button>
            </div>
          </div>
        ) : null}
        {writesAllowed ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <Label htmlFor={`case-${caseId}-event-link`}>Event id</Label>
              <Input
                id={`case-${caseId}-event-link`}
                value={eventIdToLink}
                onChange={(event) => setEventIdToLink(event.target.value)}
                placeholder="Existing event id"
              />
            </div>
            <Button
              disabled={linkMutation.isPending || eventIdToLink.trim() === ""}
              onClick={() => {
                const parsed = Number(eventIdToLink);
                if (!Number.isFinite(parsed) || parsed < 1 || String(parsed) !== eventIdToLink.trim()) {
                  showToast({ tone: "error", message: "Event id must be a positive integer." });
                  return;
                }
                linkMutation.mutate(parsed);
              }}
            >
              Link event
            </Button>
          </div>
        ) : null}
        {eventsQuery.isLoading ? <p className="text-sm text-slate-600">Loading quality events…</p> : null}
        {eventsQuery.isError ? <p className="text-sm text-red-700">{(eventsQuery.error as Error).message}</p> : null}
        {!eventsQuery.isLoading && !eventsQuery.isError && events.length === 0 ? (
          <p className="text-sm text-slate-600">
            {filtersActive ? "No rows match these filters." : "No quality events linked to this case."}
          </p>
        ) : null}
        {events.length > 0 ? (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2">Event</th>
                  <th className="px-2 py-2">Source</th>
                  <th className="px-2 py-2">Summary</th>
                  <th className="px-2 py-2">Opened</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-b border-slate-100">
                    <td className="px-2 py-2">
                      <Link className="text-sky-700 hover:underline" to={`/quality/events/${event.id}`}>
                        {qualityEventLabel(event.id)}
                      </Link>
                    </td>
                    <td className="px-2 py-2">{event.source ?? EM_DASH}</td>
                    <td className="px-2 py-2">{event.summary ?? EM_DASH}</td>
                    <td className="px-2 py-2">{formatDate(event.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

export function QualityCaseLink({ caseId }: { caseId: number }): JSX.Element {
  return (
    <Link className="text-sky-700 hover:underline" to={`/quality/cases/${caseId}`}>
      {qualityCaseLabel(caseId)}
    </Link>
  );
}
